//src/components/programs/EnduranceProgram.jsx
import React, { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import EnduranceImage from "../../assets/Programs/Pillar1.png";
import { Link } from "react-router-dom";

export default function EnduranceProgram() {
  const [showSelection, setShowSelection] = useState(false);
  const [level, setLevel] = useState("");
  const [weeks, setWeeks] = useState("");

  const bulletPoints = [
    "Develop stable aerobic capacity through structured volume phases.",
    "Improve steady-force output with controlled intensity increases.",
    "Build pacing awareness for consistent performance under fatigue.",
    "Learn session planning methods that support long-term progression.",
    "Use monitored workload cycles to reduce risk of overtraining.",
    "Strengthen recovery efficiency with planned low-intensity days.",
  ];

  const panelRef = useRef(null);
  useEffect(() => {
    if (showSelection && panelRef.current) {
      const rect = panelRef.current.getBoundingClientRect();
      const panelTop = rect.top + window.scrollY;
      const panelBottom = rect.bottom + window.scrollY;
      const viewportHeight = window.innerHeight;

      // Only scroll if the panel is not fully visible
      if (rect.bottom > viewportHeight) {
        const scrollY = panelBottom - viewportHeight + 20; // 20px padding
        window.scrollTo({ top: scrollY, behavior: "smooth" });
      } else if (rect.top < 0) {
        const scrollY = panelTop - 20; // scroll up slightly if top is off-screen
        window.scrollTo({ top: scrollY, behavior: "smooth" });
      }
    }
  }, [showSelection]);

  return (
    <section id="endurance_programs" className="w-full bg-black/40 py-24 flex flex-col items-center relative">
      {/* Separator 
      <div className="w-full flex justify-center mt-16 mb-20">
        <div className="w-4/5 h-1 rounded-full bg-gradient-to-r from-gray-700 via-gray-500 to-gray-700 shadow-lg"></div>
      </div>*/}

      {/* Glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[55%] h-[40%] bg-neonTurquoise/10 blur-[120px] rounded-full pointer-events-none"></div>

      {/* CONTENT */}
      <div className="w-full max-w-[80%] mx-auto flex flex-col md:flex-row gap-16 items-start relative z-20">
        {/* IMAGE */}
        <motion.div
          className="md:w-1/2 flex justify-center"
          whileHover={{ scale: 1.03 }}
        >
          <img
            src={EnduranceImage}
            alt="Endurance"
            className="rounded-2xl shadow-[0_0_30px_rgba(0,255,255,0.25)] w-full object-cover"
          />
        </motion.div>

        {/* TEXT */}
        <motion.div
          className="md:w-1/2 flex flex-col gap-6"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
          <h2 className="text-white text-5xl font-bold tracking-wide drop-shadow-xl">
            Endurance Programs
          </h2>

          <p className="text-gray-300 text-lg leading-relaxed">
            The Endurance Program uses phased training to build aerobic
            capacity, improve stamina, and strengthen cardiovascular and
            muscular endurance safely through controlled intensity and
            consistent, progressive workload.
          </p>

          <p className="text-gray-300 text-lg leading-relaxed">
            Participants also learn to monitor their performance and recovery,
            ensuring sustainable growth over time. The plan combines periodized
            training cycles with measurable goals to track progress, making it
            suitable for both beginners and advanced athletes seeking steady
            improvement and long-term conditioning benefits.
          </p>

          <h3 className="text-neonTurquoise text-2xl font-semibold">
            What you will build in this plan:
          </h3>

          <ul className="flex flex-col gap-3 text-gray-300 text-lg ml-4">
            {bulletPoints.map((item, i) => (
              <li key={i} className="leading-relaxed flex">
                <span className="text-neonTurquoise mr-2">•</span>
                {item}
              </li>
            ))}
          </ul>
        </motion.div>
      </div>

      {/* BUTTON */}
      <motion.button
        onClick={() => setShowSelection(!showSelection)}
        whileHover={{ scale: 1.05 }}
        className="mt-16 px-10 py-4 bg-neonTurquoise text-black font-bold rounded-full text-lg shadow-lg hover:bg-neonTurquoise/80 transition tracking-wide"
      >
        Select Your Endurance Plan
      </motion.button>

      {/* PANEL */}
      {showSelection && (
        <motion.div
        ref={panelRef}
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="
            mt-10
            backdrop-blur-xl
            bg-white/10
            border border-neonTurquoise/40
            rounded-2xl
            px-10 py-8
            shadow-[0_0_35px_rgba(0,255,255,0.25)]
            flex flex-col md:flex-row
            gap-10 items-center
            w-full max-w-[45%] mx-auto
          "
        >
          {/* LEVEL SELECT */}
          <div className="flex flex-col items-start gap-2 w-full max-w-xs">
            <label className="text-white font-semibold text-lg">Level</label>
            <div className="relative w-full">
              <select
                className="
                  bg-gray-900 text-white w-full px-4 py-3 rounded-xl
                  border border-gray-700 shadow
                  focus:ring-2 focus:ring-neonTurquoise
                  outline-none appearance-none
                "
                value={level}
                onChange={(e) => setLevel(e.target.value)}
              >
                <option value="">Select Level</option>
                <option value="beginner">Beginner</option>
                <option value="advanced">Advanced</option>
              </select>

              {/* Custom Arrow */}
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-300 pointer-events-none">
                ▼
              </span>
            </div>
          </div>

          {/* WEEKS SELECT */}
          <div className="flex flex-col items-start gap-2 w-full max-w-xs">
            <label className="text-white font-semibold text-lg">Duration</label>
            <div className="relative w-full">
              <select
                className="
                  bg-gray-900 text-white w-full px-4 py-3 rounded-xl
                  border border-gray-700 shadow
                  focus:ring-2 focus:ring-neonTurquoise
                  outline-none appearance-none
                "
                value={weeks}
                onChange={(e) => setWeeks(e.target.value)}
              >
                <option value="">Select Weeks</option>
                <option value="6">6 Weeks</option>
                <option value="8">8 Weeks</option>
                <option value="12">12 Weeks</option>
              </select>

              {/* Custom Arrow */}
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-300 pointer-events-none">
                ▼
              </span>
            </div>
          </div>

          {/* VIEW PLAN */}
          {level && weeks && (
            <Link
              to={`/endurance/${level}?weeks=${weeks}`}
              className="px-8 py-3 bg-neonTurquoise text-black font-bold rounded-full shadow-lg hover:bg-neonTurquoise/80 transition whitespace-nowrap"
            >
              View Plan
            </Link>
          )}
        </motion.div>
      )}
    </section>
  );
}
